import { useMemo } from 'react';

import { isResourceName } from '@spotify/confidence-core-react';
import type { ChatEventFragment } from '@spotify/confidence-graphql';
import {
  createCodeBlockExtension,
  useChatContext,
} from '@spotify/plugin-confidence-chat-react';

import { recordingsPlugin } from '../plugin';
import { useRecordingsCodeBlockComponent } from './RecordingsCodeBlockContext';
import { RecordingsResult } from './RecordingsResult';
import { parseRecordingReferences } from './parseRecordingReferences';
import { parseSearchResult } from './searchResult';
import type { RecordingHit, RecordingReference } from './types';

const RECORDING_CODE_BLOCK_LANGUAGES = ['recording', 'recordings'];

const collectSearchedRecordings = (
  events: readonly ChatEventFragment[],
): ReadonlyMap<string, RecordingHit> => {
  const hits = new Map<string, RecordingHit>();
  for (const event of events) {
    const result = parseSearchResult(event.toolResult?.content);
    for (const hit of result?.recordings ?? []) {
      hits.set(hit.name, hit);
    }
  }
  return hits;
};

export type RecordingsCodeBlockProps = {
  references: RecordingReference[];
};

export const RecordingsCodeBlock = ({
  references,
}: RecordingsCodeBlockProps) => {
  const chatContext = useChatContext();
  const chatEvents = chatContext?.chatEvents;
  const searchedRecordings = useMemo(
    () => collectSearchedRecordings(chatEvents ?? []),
    [chatEvents],
  );
  const recordings = useMemo(
    () =>
      references.flatMap((ref): RecordingHit[] => {
        const searchedRecording = searchedRecordings.get(ref.name);
        if (chatContext?.isStreaming && !searchedRecording) return [];
        if (searchedRecording) {
          return [
            {
              ...searchedRecording,
              findingId: ref.findingId,
              startTime: ref.startTime,
              endTime: ref.endTime,
            },
          ];
        }
        return [
          {
            name: ref.name,
            display_name: '',
            findingId: ref.findingId,
            startTime: ref.startTime,
            endTime: ref.endTime,
          },
        ];
      }),
    [references, searchedRecordings, chatContext?.isStreaming],
  );

  return <RecordingsResult recordings={recordings} />;
};

const RecordingsCodeBlockView = (props: RecordingsCodeBlockProps) => {
  const Component = useRecordingsCodeBlockComponent() ?? RecordingsCodeBlock;
  return <Component {...props} />;
};

export const RecordingsCodeBlockExtension = recordingsPlugin.provide(
  createCodeBlockExtension({
    id: 'recordings-code-block',
    predicate: ({ language }) =>
      RECORDING_CODE_BLOCK_LANGUAGES.includes(language),
    parseLegacyContent: content => {
      const names = content
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean);
      return names.length > 0 &&
        names.every(name => isResourceName(name, 'recordings'))
        ? { language: 'recordings', content: names.join('\n') }
        : null;
    },
    render: ({ content }) => {
      const references = parseRecordingReferences(content);
      return references.length > 0 ? (
        <RecordingsCodeBlockView references={references} />
      ) : null;
    },
  }),
);
